import { useContext, useSyncExternalStore } from "react";
import HideDataContext from "../helpers/hideDataContext";
import {
  getSnapshot,
  isClassMasked,
  setEnabled,
  subscribe,
} from "../helpers/hideData";

// Privacy Mode state.
//
// Reads the store in helpers/hideData.js rather than the Redux user: hanging it off
// `state.Auth.user` would give that object a new identity on every toggle, and 60 effects in
// this app depend on `[user]`, 26 of which refetch — the screen behaved as if it had reloaded.
//
// useSyncExternalStore is the right primitive here because it reads the SAME state the hook-free
// masking points read (the `…Text()` formatters, the d3 and Plotly drawing). The provider used
// to hold it in useState and mirror it into a module variable during render — two truths, two
// `isMasked` implementations, and a side effect in a function React may call more than once.
//
// Two writers, because the two halves live in different places:
//   setHidden(bool)     the switch — device-local, instant, no database write
//   saveConfig(prefs)   classes / treatment — an ordinary settings write
//
// saveConfig needs Redux, the GraphQL client and the current user, so it still comes from the
// context (HideDataProvider). Nothing else needs a provider any more.
export const useHideData = () => {
  const config = useSyncExternalStore(subscribe, getSnapshot);
  const { saveConfig } = useContext(HideDataContext);

  return {
    config,
    hidden: config.enabled,
    treatment: config.treatment,
    // The module function on purpose, not a local copy: it reads the same snapshot this hook
    // subscribed to, so the JSX path and the string path can never disagree.
    isMasked: isClassMasked,
    saveConfig,
    setHidden: setEnabled,
    toggle: () => setEnabled(!config.enabled),
  };
};

export default useHideData;
